import React from 'react';
import { Globe, LayoutDashboard, Building2 } from 'lucide-react';
import { useTenant } from '../../contexts/TenantContext';
import { useCurrentUser } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Badge } from '../ui/Badge';
import { cn } from '../../lib/utils';

/**
 * ContextBar
 *
 * A persistent, always-visible statement of *which organization the app is
 * currently scoped to*. The app has three implicit contexts — the platform root,
 * the aggregated "all organizations" view, and a specific org — that previously
 * looked identical once you were inside the shell. That ambiguity is the root of
 * "why can't I see my stuff": the same URL renders different modules, data and
 * permissions depending on context, with no on-screen explanation.
 *
 * Rendered once in the authenticated shell, below the Navbar.
 */
export const ContextBar: React.FC = () => {
  const { node, isGlobal, isUniversal } = useTenant();
  const { isSignedIn, memberships = [] } = useCurrentUser();
  const { role } = usePermissions();

  if (!isSignedIn) return null;

  let Icon = Building2;
  let label = node?.name || 'Organization';
  let detail = `Your role: ${role}`;
  let accent = 'bg-[#FFD600] text-black';

  if (isUniversal) {
    Icon = LayoutDashboard;
    label = 'All organizations';
    detail = `${memberships.length} ${memberships.length === 1 ? 'organization' : 'organizations'}`;
    accent = 'bg-[#0044FF] text-white';
  } else if (isGlobal || !node) {
    Icon = Globe;
    label = 'Platform';
    detail = 'No organization selected — pick one to use its tools';
    accent = 'bg-black text-white dark:bg-white dark:text-black';
  }

  return (
    <div
      className="flex items-center gap-3 px-4 md:px-6 h-11 shrink-0 bg-white dark:bg-black border-b-4 border-black dark:border-white"
      data-context={isUniversal ? 'universal' : isGlobal || !node ? 'platform' : 'org'}
    >
      <span className="text-[10px] font-black font-mono uppercase tracking-[0.2em] text-gray-400 shrink-0">
        Viewing
      </span>
      <span
        className={cn(
          'w-6 h-6 border-2 border-black flex items-center justify-center shrink-0',
          accent
        )}
      >
        <Icon size={13} />
      </span>
      <span className="truncate text-[11px] font-black uppercase tracking-widest font-mono text-black dark:text-white">
        {label}
      </span>
      {!isUniversal && !isGlobal && node?.tier && (
        <Badge variant="blue" className="shrink-0 hidden sm:inline-flex">
          {node.tier}
        </Badge>
      )}
      <span className="ml-auto min-w-0 truncate text-[10px] font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">
        {detail}
      </span>
    </div>
  );
};
